import type { NotificationItem } from "./notifications";

/**
 * The notifications that arrived since the inbox was last looked at: unread ones not seen
 * before, oldest first, so they stack in the order they came. Every id is added to `seen`.
 * The first look (`seen` null) only remembers what is there: nothing is new on page load.
 */
export function newArrivals(items: NotificationItem[], seen: Set<string> | null): NotificationItem[] {
  if (!seen) return [];
  const fresh = items.filter((item) => !item.read_at && !seen.has(item.id));
  for (const item of items) seen.add(item.id);
  return fresh.reverse();
}

/** Where the sound setting is kept, per browser: on unless switched off. */
export const NOTIFICATION_SOUND_KEY = "powwater.notification-sound";

let context: AudioContext | undefined;

/**
 * A short two-note chime for a new notification, made with Web Audio (no file to load). Quiet
 * on purpose. Browsers play it only once the person has interacted with the page; before that
 * it stays silent, and that's fine.
 */
export function playChime() {
  try {
    context ??= new AudioContext();
    const ctx = context;
    void ctx.resume();
    const start = ctx.currentTime + 0.01;
    for (const [i, frequency] of [880, 1318.5].entries()) {
      const at = start + i * 0.12;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0, at);
      gain.gain.linearRampToValueAtTime(0.12, at + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.45);
      osc.connect(gain).connect(ctx.destination);
      osc.start(at);
      osc.stop(at + 0.5);
    }
  } catch {
    // no Web Audio here: the toast still shows
  }
}
